import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import type { ChannelProductAssignment } from '../types'
import type { CollaborationRole } from '../utils/collaborationRoles'

import { GetPendingInvitations } from './usePendingInvitations'

export const InviteTeamMembers = graphql(/* GraphQL */ `
  mutation InviteTeamMembers($input: InviteTeamMembersInput!) {
    inviteTeamMembers(input: $input) {
      ... on InviteTeamMembersSuccess {
        created {
          id
          email
          name
        }
        failed {
          email
          reason
        }
      }
      ... on TeamMemberLimitReachedError {
        message
      }
    }
  }
`)

type TeamMemberRole = 'admin' | 'member'

/**
 * One invite. An organization on the Roles model sends the member's
 * `collaborationRole` and the `channelIds` they get access to (none for an
 * Admin, who gets every channel on acceptance); a legacy organization sends
 * the admin/member `role` with per-product `channelAssignments`. Only the
 * fields the caller sets are sent, so the server reads the right ones for
 * the organization's permission model.
 */
export type InviteTeamMembersInput = {
  organizationId: string
  email: string
  name: string
  channelAssignments: ChannelProductAssignment[]
} & (
  | {
      role: TeamMemberRole
      collaborationRole?: undefined
      channelIds?: undefined
    }
  | {
      collaborationRole: CollaborationRole
      role?: undefined
      channelIds?: string[]
    }
)

interface UseInviteTeamMembersReturn {
  inviteTeamMember: (input: InviteTeamMembersInput) => Promise<{
    success: boolean
    error?: string
  }>
  loading: boolean
}

/**
 * Calls the `inviteTeamMembers` mutation, which writes an `Invitation` doc
 * rather than provisioning the account immediately. The dialog signature
 * is single-invite for now; the plural mutation supports batch but the
 * UI doesn't emit batches yet.
 */
export const useInviteTeamMembers = (): UseInviteTeamMembersReturn => {
  const [mutate, { loading }] = useMutation(InviteTeamMembers)

  const inviteTeamMember = async (
    input: InviteTeamMembersInput,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data } = await mutate({
        variables: {
          input: {
            organizationId: input.organizationId,
            invitations: [
              {
                email: input.email,
                name: input.name,
                ...(input.role !== undefined ? { role: input.role } : {}),
                ...(input.collaborationRole !== undefined
                  ? { collaborationRole: input.collaborationRole }
                  : {}),
                ...(input.channelIds !== undefined
                  ? { channelIds: input.channelIds }
                  : {}),
                // Each assignment carries an explicit per-product grant.
                channelAssignments: input.channelAssignments.map((ca) => ({
                  channelId: ca.channelId,
                  productAccessLevels: {
                    publish: ca.productAccessLevels.publish,
                    comments: ca.productAccessLevels.comments,
                  },
                })),
              },
            ],
          },
        },
        refetchQueries: [
          {
            query: GetPendingInvitations,
            variables: { input: { organizationId: input.organizationId } },
          },
        ],
      })

      if (!data?.inviteTeamMembers) {
        return { success: false, error: 'Failed to invite team member' }
      }

      const result = data.inviteTeamMembers

      if (result.__typename === 'InviteTeamMembersSuccess') {
        const firstFailure = result.failed[0]
        if (firstFailure) {
          return { success: false, error: firstFailure.reason }
        }
        return { success: true }
      }

      if (result.__typename === 'TeamMemberLimitReachedError') {
        return { success: false, error: result.message }
      }

      return { success: false, error: 'Unknown error occurred' }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'An error occurred',
      }
    }
  }

  return {
    inviteTeamMember,
    loading,
  }
}
